import React, { useContext } from 'react';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  askVennPanelOpenAtom,
  askVennPendingMessageAtom,
  blockDisplayHeader,
  blockExpandedState,
  isReportState,
  isSelectedBlockState,
  selectedBlocksState,
} from 'venn-state';
import { SpecialCssClasses, useHasFF } from 'venn-utils';
import { UserContext } from '../../../../contexts';
import { DRAG_HANDLE_CLASS } from '../../../../utils';
import { useAppPrintMode } from '../../../../print';
import styled from 'styled-components';
import { Checkbox } from '@/components/ui/checkbox';
import { Icon, ZIndex } from 'venn-ui-kit';
import { cn } from '@/lib/utils';

interface BlockHeaderControlsProps {
  id: string;
}

export const BlockHeaderControls = ({ id }: BlockHeaderControlsProps) => {
  const isBlockSelected = useRecoilValue(isSelectedBlockState(id));
  const hasAskVenn = useHasFF('ask_venn_ff');
  const hasBlockAssignment = useHasFF('block_assignment_ff');
  const { inPrintMode } = useAppPrintMode();
  const isReport = useRecoilValue(isReportState);
  const isExpanded = useRecoilValue(blockExpandedState(id));
  const { hasPermission } = useContext(UserContext);
  const blockTitle = useRecoilValue(blockDisplayHeader(id));
  const setAskVennOpen = useSetRecoilState(askVennPanelOpenAtom);
  const setPendingMessage = useSetRecoilState(askVennPendingMessageAtom);

  const toggleBlock = useRecoilCallback(
    ({ set }) =>
      () => {
        if (isBlockSelected) {
          set(selectedBlocksState, (current) => current.filter((otherId) => otherId !== id));
        } else {
          set(selectedBlocksState, (current) => [...current, id]);
        }
      },
    [isBlockSelected, id],
  );

  const onExplainBlock = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAskVennOpen(true);
    setPendingMessage(`Explain the "${blockTitle}" visualization. What does it show and what are the key takeaways?`);
  };

  const onCheckBlock = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleBlock();
  };

  const onCheckKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      toggleBlock();
    }
  };

  return (
    <div className="flex items-center">
      {hasAskVenn && !inPrintMode && (
        <button
          type="button"
          onClick={onExplainBlock}
          className="relative flex items-center gap-[5px] h-[20px] px-[7px] rounded-[3px] border-none bg-venn-dark-blue text-white font-medium cursor-pointer opacity-0 group-hover/block:opacity-100 transition-opacity hover:bg-venn-deep-dark-blue hover:text-white mr-[6px]"
          style={{ fontSize: 12 }}
          aria-label="Explain block"
        >
          <Icon type="sparkles" prefix="fas" style={{ fontSize: 10 }} />
          Explain
        </button>
      )}
      {hasBlockAssignment && !inPrintMode && (
        <div
          role="checkbox"
          aria-checked={isBlockSelected}
          aria-label="Select block"
          tabIndex={-1}
          onKeyDown={onCheckKeyDown}
          onClick={onCheckBlock}
          className="relative w-[34px] h-[34px] flex items-center justify-center group/checkbox cursor-pointer"
        >
          <HoverCircle bgClass="bg-venn-deep-dark-blue/10" group="checkbox" />
          <StyledCheckbox
            state={isBlockSelected ? 'checked' : 'unchecked'}
            className={cn(
              'group-hover/checkbox:border-venn-deep-light-blue',
              isBlockSelected ? 'group-hover/checkbox:bg-venn-deep-light-blue' : '',
            )}
          />
        </div>
      )}
      {hasBlockAssignment && isReport && <div aria-hidden="true" className="h-[16px] w-px bg-venn-grey-30" />}
      {isReport && !inPrintMode && hasPermission('STUDIO_REORDER_BLOCKS') && (
        <div
          className={cn(
            'relative w-[34px] h-[34px] flex items-center justify-center group/handle',
            isExpanded ? 'cursor-not-allowed opacity-40' : 'cursor-grab active:cursor-grabbing',
            !isExpanded && DRAG_HANDLE_CLASS,
          )}
        >
          {!isExpanded && <HoverCircle bgClass="bg-venn-grey-10" group="handle" />}
          <DragHandle type="arrows" />
        </div>
      )}
    </div>
  );
};

const StyledCheckbox = styled(Checkbox)`
  .${SpecialCssClasses.ExportAsImage} & {
    display: none;
  }
`;

const DragHandle = styled(Icon)`
  color: rgba(0, 0, 0, 0.6);
  font-size: 1rem;
  z-index: ${ZIndex.Cover};
`;

interface HoverCircleProps {
  bgClass: string;
  group: 'checkbox' | 'handle';
}

const HoverCircle: React.FC<HoverCircleProps> = ({ bgClass, group }) => {
  // Use literal classes so Tailwind can statically detect them
  const hoverClass = group === 'checkbox' ? 'group-hover/checkbox:scale-100' : 'group-hover/handle:scale-100';

  return (
    <span
      aria-hidden="true"
      className={cn(
        'absolute inset-0 rounded-full',
        bgClass,
        'scale-0',
        hoverClass,
        'transition-transform duration-200 ease-in-out origin-center pointer-events-none will-change-transform z-0',
      )}
    />
  );
};
